import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { LEAVE_MS } from './components/brand/threshold';
import { ErrorBoundary } from './components/ErrorBoundary';
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import './styles/globals.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

/*
  #boot из index.html — ядро до загрузки скрипта (R42). Сцена первой загрузки (BootScene) забирает его сама
  (data-taken) и снимает, проявившись поверх. Не забрала — после первой отрисовки слой гаснет за LEAVE_MS и
  уходит; не успел показаться (приложение готово раньше 400 мс) — уходит, так и не показавшись.
  Страница, открытая во фоновой вкладке, кадров не рисует — rAF стоит, а CSS-анимация слоя идёт, и к
  возвращению он уже непрозрачен. Поэтому вкладка, ставшая видимой с готовым приложением, снимает слой сразу,
  до первого кадра: тёмная сцена над собранной страницей не мелькает.
*/
function bootLayer(): HTMLElement | null {
  const boot = document.getElementById('boot');
  return boot && boot.dataset.taken === undefined ? boot : null;
}
const onShown = () => {
  if (document.visibilityState !== 'visible') return;
  document.removeEventListener('visibilitychange', onShown);
  if (rootEl.childElementCount > 0) bootLayer()?.remove();
};
document.addEventListener('visibilitychange', onShown);
requestAnimationFrame(() =>
  requestAnimationFrame(() => {
    document.removeEventListener('visibilitychange', onShown);
    const boot = bootLayer();
    if (!boot) return;
    const from = getComputedStyle(boot).opacity;
    boot.getAnimations().forEach((a) => a.cancel());
    const fade = boot.animate([{ opacity: from }, { opacity: 0 }], { duration: LEAVE_MS, easing: 'ease-in' });
    fade.onfinish = () => boot.remove();
  }),
);
